import type { FC, HTMLAttributes } from "react";
import { useState } from "react";
import {
    Button as AriaButton,
    DialogTrigger as AriaDialogTrigger,
    Popover as AriaPopover,
} from "react-aria-components";
import type { NavItemType } from "../config";
import { NavItemBase } from "./nav-item";
import { isRouteActive } from "@/utils/route-matcher";
import { cx, sortCx } from "@/utils/cx";

const styles = sortCx({
    root: "group relative flex w-full cursor-pointer items-center rounded-md bg-primary outline-focus-ring transition duration-100 ease-linear select-none hover:bg-primary_hover focus-visible:z-10 focus-visible:outline-2 focus-visible:outline-offset-2",
    rootSelected: "bg-active hover:bg-secondary_hover",
});

interface NavCollapsedMacroPopoverProps {
    item: NavItemType;
    activeUrl?: string;
}

/** Icon strip for macro-area trigger (aligned with NavItemBase collapsed). */
function MacroTriggerIcon({
    current,
    badge,
    icon: Icon,
    iconSrc,
    iconHoverSrc,
}: {
    current?: boolean;
    badge?: NavItemType["badge"];
    icon?: FC<HTMLAttributes<HTMLOrSVGElement>>;
    iconSrc?: string;
    iconHoverSrc?: string;
}) {
    const isBadgeReactNode = badge && typeof badge !== "string" && typeof badge !== "number";

    if (!(iconSrc || Icon)) {
        return null;
    }

    return (
        <span className="relative inline-flex">
            {iconSrc ? (
                <>
                    <img
                        src={iconSrc}
                        alt=""
                        aria-hidden="true"
                        className={cx(
                            "size-5 shrink-0 transition-inherit-all",
                            (current || iconHoverSrc) && "group-hover:hidden",
                            current && iconHoverSrc && "hidden",
                        )}
                    />
                    {iconHoverSrc && (
                        <img
                            src={iconHoverSrc}
                            alt=""
                            aria-hidden="true"
                            className={cx(
                                "size-5 shrink-0 transition-inherit-all",
                                current ? "block" : "hidden group-hover:block",
                            )}
                        />
                    )}
                </>
            ) : (
                Icon && <Icon aria-hidden="true" className="size-5 shrink-0 text-fg-quaternary transition-inherit-all" />
            )}
            {isBadgeReactNode && badge}
        </span>
    );
}

export function NavCollapsedMacroPopover({ item, activeUrl }: NavCollapsedMacroPopoverProps) {
    const [isOpen, setIsOpen] = useState(false);
    const items = item.items!;
    const current =
        (item.href && isRouteActive(item.href, activeUrl)) ||
        items.some((sub) => isRouteActive(sub.href, activeUrl));

    return (
        <AriaDialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
            <AriaButton
                aria-label={item.label}
                className={cx("flex w-full px-3 py-2 justify-center", styles.root, current && styles.rootSelected)}
            >
                <MacroTriggerIcon
                    current={current}
                    badge={item.badge}
                    icon={item.icon}
                    iconSrc={item.iconSrc}
                    iconHoverSrc={item.iconHoverSrc}
                />
            </AriaButton>
            <AriaPopover
                aria-label={item.label}
                placement="right top"
                offset={8}
                className={({ isEntering, isExiting }) =>
                    cx(
                        "z-50 min-w-56 max-w-xs origin-(--trigger-anchor-point) overflow-auto rounded-lg bg-primary p-2 shadow-lg ring-1 ring-secondary_alt will-change-transform",
                        isEntering &&
                            "duration-150 ease-out animate-in fade-in placement-right:slide-in-from-left-0.5 placement-top:slide-in-from-bottom-0.5 placement-bottom:slide-in-from-top-0.5",
                        isExiting &&
                            "duration-100 ease-in animate-out fade-out placement-right:slide-out-to-left-0.5 placement-top:slide-out-to-bottom-0.5 placement-bottom:slide-out-to-top-0.5",
                    )
                }
            >
                <div className="border-b border-secondary px-2 pb-2 pt-1">
                    <p className="text-xs font-semibold text-tertiary">{item.label}</p>
                </div>
                <ul className="flex flex-col py-1">
                    {items.map((child) => (
                        <li key={child.label} className="py-0.5">
                            <NavItemBase
                                href={child.href}
                                badge={child.badge}
                                icon={child.icon}
                                iconSrc={child.iconSrc}
                                iconHoverSrc={child.iconHoverSrc}
                                type="link"
                                current={isRouteActive(child.href, activeUrl)}
                                isCollapsed={false}
                                onClick={() => setIsOpen(false)}
                            >
                                {child.label}
                            </NavItemBase>
                        </li>
                    ))}
                </ul>
            </AriaPopover>
        </AriaDialogTrigger>
    );
}
